'use client';

import { useQuery } from '@tanstack/react-query';
import { Address } from 'viem';
import { ChainType } from '@/constants/chain-specific';
import { getTokenPrices } from '@/lib/price-data/tokenPrice';
import type { TokenConfigType } from '@/constants/types/currency';
interface TokenPrice {
  address: Address;
  priceUsd: number;
  priceChange24h?: number;
  volume24h?: number;
  marketCap?: number;
}
interface TokenPricesResult {
  prices: Record<Address, TokenPrice>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch token prices for liquidity position valuation using GeckoTerminal API
 */
export function useTokenPrices(tokenAddresses: Address[], chainId: ChainType = 109): TokenPricesResult {
  const {
    data: prices = {},
    isLoading,
    error: error_0,
    refetch
  } = useQuery({
    queryKey: ['tokenPrices', tokenAddresses, chainId],
    queryFn: async (): Promise<Record<Address, TokenPrice>> => {
      if (tokenAddresses.length === 0) return {};
      try {
        console.log(`🔄 Fetching prices for ${tokenAddresses.length} tokens on chain ${chainId}:`, tokenAddresses.map(addr => addr.slice(0, 6) + '...').join(', '));

        // Convert addresses to TokenConfigType format expected by getTokenPrices
        const tokens: TokenConfigType[] = tokenAddresses.map(address => ({
          address,
          name: 'Unknown',
          // We don't need these for price fetching
          symbol: 'UNK',
          decimals: 18
        }));

        // Use the existing GeckoTerminal API implementation
        const priceData = await getTokenPrices(chainId, tokens);

        // Convert the response to our expected format
        const formattedPrices: Record<Address, TokenPrice> = {};
        for (const address_0 of tokenAddresses) {
          const priceUsd = priceData[address_0.toLowerCase()] || 0;
          formattedPrices[address_0] = {
            address: address_0,
            priceUsd,
            priceChange24h: undefined,
            // GeckoTerminal basic endpoint doesn't include these
            volume24h: undefined,
            marketCap: undefined
          };
          if (priceUsd > 0) {
            console.log(`💰 ${address_0.slice(0, 6)}...: $${priceUsd.toFixed(6)}`);
          } else {
            console.warn(`⚠️ No price data for token ${address_0.slice(0, 6)}...`);
          }
        }
        console.log(`✅ Successfully fetched prices for ${Object.keys(formattedPrices).length} tokens`);
        const validPrices = Object.values(formattedPrices).filter(p => p.priceUsd > 0).length;
        console.log(`📊 ${validPrices}/${tokenAddresses.length} tokens have valid price data`);
        return formattedPrices;
      } catch (error) {
        console.error('❌ Error fetching token prices:', error);
        throw error;
      }
    },
    enabled: tokenAddresses.length > 0,
    staleTime: 60000,
    // 1 minute (GeckoTerminal cache duration)
    retry: 2,
    refetchInterval: 5 * 60 * 1000 // Refetch every 5 minutes
  });
  return {
    prices,
    isLoading,
    error: error_0,
    refetch
  };
}

/**
 * Calculate the USD value of a liquidity position
 */
export function calculatePositionValue(token0Amount: string, token1Amount: string, token0Price: number, token1Price: number): number {
  const value0 = parseFloat(token0Amount) * token0Price;
  const value1 = parseFloat(token1Amount) * token1Price;
  return value0 + value1;
}

/**
 * Calculate the current price ratio between two tokens
 */
export function calculatePriceRatio(reserve0: bigint, reserve1: bigint, decimals0: number, decimals1: number): number {
  if (reserve0 === 0n) return 0;
  const reserve0Formatted = Number(reserve0) / Math.pow(10, decimals0);
  const reserve1Formatted = Number(reserve1) / Math.pow(10, decimals1);
  return reserve1Formatted / reserve0Formatted;
}

/**
 * Calculate APR based on fee collection (simplified)
 */
export function calculateAPR(positionValueUsd: number, dailyFeesUsd: number): number {
  if (positionValueUsd === 0) return 0;
  const dailyRate = dailyFeesUsd / positionValueUsd;
  const annualRate = dailyRate * 365;
  return annualRate * 100; // Convert to percentage
}